import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus, 
  Edit, 
  Trash2,
  Shield,
  Mail,
  User
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const UsersPage = () => {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'service'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'service'
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        const updateData = { name: formData.name, role: formData.role };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('Benutzer aktualisiert');
      } else {
        await api.post('/users', formData);
        toast.success('Benutzer erstellt');
      }
      setDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await api.delete(`/users/${deleteId}`);
      setUsers(users.filter(u => u.id !== deleteId));
      toast.success('Benutzer gelöscht');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setDeleteId(null);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      office: 'Büro',
      service: 'Service',
      kitchen: 'Küche'
    };
    return labels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-burgundy text-white',
      office: 'bg-gold text-charcoal-900',
      service: 'bg-emerald-600 text-white',
      kitchen: 'bg-orange-500 text-white'
    };
    return colors[role] || 'bg-stone-500 text-white';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-burgundy">Wird geladen...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-burgundy flex items-center gap-3">
            <Shield className="h-8 w-8" />
            Benutzerverwaltung
          </h1>
          <p className="text-charcoal-500 mt-1">
            {users.length} {users.length === 1 ? 'Benutzer' : 'Benutzer'} registriert
          </p>
        </div>
        
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-burgundy hover:bg-burgundy-800 text-white font-serif"
          data-testid="add-user-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Neuer Benutzer
        </Button>
      </div>

      {/* Role Legend */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <span className="text-sm text-charcoal-600 font-medium">Rollen:</span>
            <Badge className="bg-burgundy text-white">Admin - Alle Rechte</Badge>
            <Badge className="bg-gold text-charcoal-900">Büro - Erstellen & Freigeben</Badge>
            <Badge className="bg-emerald-600 text-white">Service - Ansehen & Download</Badge>
            <Badge className="bg-orange-500 text-white">Küche - Ansehen & Download</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="shadow-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-serif text-burgundy">Name</TableHead>
              <TableHead className="font-serif text-burgundy">E-Mail</TableHead>
              <TableHead className="font-serif text-burgundy">Rolle</TableHead>
              <TableHead className="font-serif text-burgundy">Erstellt am</TableHead>
              <TableHead className="font-serif text-burgundy text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow 
                key={user.id} 
                className="hover:bg-stone-50 transition-colors"
                data-testid={`user-row-${user.id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-burgundy-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-burgundy" />
                    </div>
                    <span className="font-semibold text-charcoal-800">{user.name}</span>
                    {user.id === currentUser?.id && (
                      <Badge variant="outline" className="text-xs">Sie</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 text-charcoal-600">
                    <Mail className="h-4 w-4" />
                    {user.email}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={getRoleBadgeColor(user.role)}>
                    {getRoleLabel(user.role)}
                  </Badge>
                </TableCell>
                <TableCell className="text-charcoal-500">
                  {new Date(user.created_at).toLocaleDateString('de-DE')}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleOpenDialog(user)}
                      className="text-charcoal-600 hover:text-burgundy"
                      data-testid={`edit-user-${user.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    
                    {user.id !== currentUser?.id && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setDeleteId(user.id)}
                        className="text-charcoal-600 hover:text-red-600"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-burgundy">
              {editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="border-stone-300"
                data-testid="user-name-input"
              />
            </div>
            
            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="border-stone-300"
                  data-testid="user-email-input"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="password">
                {editingUser ? 'Neues Passwort (optional)' : 'Passwort'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                className="border-stone-300"
                data-testid="user-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Rolle</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="border-stone-300" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="office">Büro</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="kitchen">Küche</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-stone-300"
              >
                Abbrechen
              </Button>
              <Button 
                type="submit"
                className="bg-burgundy hover:bg-burgundy-800 text-white"
                data-testid="save-user-btn"
              >
                {editingUser ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-burgundy">
              Benutzer löschen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Der Benutzer verliert den Zugang zum System.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-stone-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
